/**
 * Multi-portal sign-in modal.
 *
 * Left rail: 3 portal cards (Admin · Emerald Books · Client Portal — coming soon).
 * Right pane: inline credential form that swaps its target API depending on
 * which portal is selected.
 *
 * On success:
 *   • Portal: refreshes AuthContext, navigates to /admin (or the `redirectTo` prop).
 *   • Books: writes books_token + books_tenant + books_user into localStorage,
 *            navigates to /books/app/dashboard.
 */
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  X, ShieldCheck, BookOpen, Users, Lock, User2, Loader2, LogIn, ChevronRight,
  Shield, UserCircle2,
} from 'lucide-react';
import { authLogin } from '../services/api';
import { booksLogin, saveBooksSession } from '../books/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

const PORTALS = [
  {
    id: 'admin',
    title: 'Admin Portal',
    subtitle: 'Emerald staff & administrators',
    hint: 'Use your emeraldcorporates.com credentials.',
    icon: ShieldCheck,
    color: '#0f5c3d',
  },
  {
    id: 'books',
    title: 'Emerald Books',
    subtitle: 'Accounting SaaS',
    hint: 'Sign in to your Books tenant.',
    icon: BookOpen,
    color: '#0b7c53',
  },
  {
    id: 'client',
    title: 'Client Portal',
    subtitle: 'Coming soon',
    hint: 'Access to your account and shared workspaces (launching soon).',
    icon: Users,
    color: '#94a3b8',
    disabled: true,
  },
];

const SignInModal = ({ open, onClose, initialPortal = 'admin' }) => {
  const [selected, setSelected] = useState(initialPortal);
  // Admin portal now has two sub-tabs: 'admin' (→ /admin) and 'employee' (→ /hr/me).
  // Same credentials, only the post-login landing differs.
  const [adminTab, setAdminTab] = useState('admin');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  const portal = useMemo(() => PORTALS.find((p) => p.id === selected) || PORTALS[0], [selected]);

  const reset = () => { setIdentifier(''); setPassword(''); setLoading(false); };

  const handleClose = () => { reset(); onClose && onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      if (portal.id === 'admin') {
        await authLogin(identifier.trim(), password);
        await refresh();
        const landing = adminTab === 'employee' ? '/hr/me' : '/admin';
        toast({
          title: 'Welcome back',
          description: adminTab === 'employee' ? 'Opening Employee Self-Service.' : 'Signed in to Admin Portal.',
        });
        handleClose();
        nav(landing);
      } else if (portal.id === 'books') {
        const res = await booksLogin(identifier.trim(), password);
        saveBooksSession(res.token, res.user, res.tenant);
        toast({ title: 'Welcome back', description: `Signed in to ${res.tenant?.company_name || 'Emerald Books'}.` });
        handleClose();
        nav('/books/app/dashboard');
      }
    } catch (err) {
      toast({
        title: 'Sign-in failed',
        description: err?.response?.data?.detail || 'Invalid credentials.',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-6"
      onClick={handleClose}
      data-testid="signin-modal"
    >
      <div
        className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden grid grid-cols-1 md:grid-cols-[260px_1fr]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left rail — portal picker */}
        <div className="bg-[#f7fbf8] border-r border-[#0f5c3d]/10 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold tracking-wide uppercase text-[#0b1a2e]/60">Sign in to</h2>
            <button
              onClick={handleClose}
              className="text-[#0b1a2e]/50 hover:text-[#0b1a2e] md:hidden"
              aria-label="Close"
              data-testid="signin-close-mobile"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            {PORTALS.map((p) => {
              const Icon = p.icon;
              const active = p.id === selected && !p.disabled;
              return (
                <button
                  key={p.id}
                  onClick={() => !p.disabled && setSelected(p.id)}
                  disabled={p.disabled}
                  data-testid={`signin-portal-${p.id}`}
                  className={
                    'w-full text-left rounded-lg border transition-all px-3 py-2.5 flex items-start gap-3 ' +
                    (p.disabled
                      ? 'border-dashed border-[#0f5c3d]/10 opacity-60 cursor-not-allowed bg-white'
                      : active
                        ? 'border-[#0f5c3d] bg-white shadow-sm ring-2 ring-[#0f5c3d]/20'
                        : 'border-transparent bg-white/60 hover:bg-white hover:border-[#0f5c3d]/20')
                  }
                >
                  <div
                    className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${p.color}18`, color: p.color }}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#0b1a2e] truncate">{p.title}</div>
                    <div className="text-xs text-[#0b1a2e]/50 truncate">{p.subtitle}</div>
                  </div>
                  {!p.disabled && active && <ChevronRight className="w-4 h-4 text-[#0f5c3d]" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right pane — credential form */}
        <div className="p-6 md:p-8 relative">
          <button
            onClick={handleClose}
            className="hidden md:block absolute top-4 right-4 text-[#0b1a2e]/40 hover:text-[#0b1a2e] transition-colors"
            aria-label="Close"
            data-testid="signin-close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="mb-6">
            <h1
              className="text-2xl md:text-3xl text-[#0b1a2e]"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
            >
              {portal.title}
            </h1>
            <p className="mt-1 text-sm text-[#0b1a2e]/60">
              {portal.id === 'admin'
                ? (adminTab === 'employee'
                    ? 'Access your Employee Self-Service portal (payslips · leave · claims · docs).'
                    : 'Access the Emerald admin & management portal.')
                : portal.hint}
            </p>
          </div>

          {/* Admin sub-tabs: Admin/Staff vs Employee. Same credentials, different landing. */}
          {portal.id === 'admin' && !portal.disabled && (
            <div className="mb-5 grid grid-cols-2 gap-1 p-1 bg-[#f2f7f4] border border-[#0f5c3d]/10 rounded-full text-xs">
              <button
                type="button"
                onClick={() => setAdminTab('admin')}
                data-testid="signin-admin-tab-admin"
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full font-semibold transition ${
                  adminTab === 'admin' ? 'bg-white text-[#0f5c3d] shadow-sm' : 'text-[#0b1a2e]/60 hover:text-[#0f5c3d]'
                }`}
              >
                <Shield className="w-3.5 h-3.5" /> Admin & Management
              </button>
              <button
                type="button"
                onClick={() => setAdminTab('employee')}
                data-testid="signin-admin-tab-employee"
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full font-semibold transition ${
                  adminTab === 'employee' ? 'bg-white text-[#0f5c3d] shadow-sm' : 'text-[#0b1a2e]/60 hover:text-[#0f5c3d]'
                }`}
              >
                <UserCircle2 className="w-3.5 h-3.5" /> Employee / Staff
              </button>
            </div>
          )}

          {portal.disabled ? (
            <div className="rounded-xl border border-dashed border-[#0f5c3d]/20 bg-[#f7fbf8] p-6 text-center">
              <Users className="w-8 h-8 mx-auto text-[#0f5c3d]/50 mb-2" />
              <p className="text-sm text-[#0b1a2e]/70">
                The Client Portal is coming soon. Once launched, you'll be able to access your account
                and shared workspaces from here.
              </p>
              <a href="mailto:info@emeraldcorporates.com" className="mt-3 inline-block text-xs text-[#0f5c3d] hover:underline">
                Interested? Get in touch →
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid={`signin-form-${portal.id}`}>
              <label className="block">
                <span className="block text-xs font-semibold tracking-wide uppercase text-[#0b1a2e]/70 mb-1.5">
                  {portal.id === 'admin' ? 'Email or username' : 'Email'}
                </span>
                <div className="relative">
                  <User2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/50" />
                  <input
                    required
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    type={portal.id === 'admin' ? 'text' : 'email'}
                    autoComplete="username"
                    data-testid={`signin-${portal.id}-id`}
                    className="w-full border border-[#0f5c3d]/15 rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                    placeholder={portal.id === 'admin' ? 'you@emeraldcorporates.com' : 'you@company.com'}
                  />
                </div>
              </label>
              <label className="block">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold tracking-wide uppercase text-[#0b1a2e]/70">Password</span>
                  <a
                    href={portal.id === 'admin' ? '/forgot-password' : '/books/forgot-password'}
                    className="text-xs text-[#0f5c3d] hover:underline"
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/50" />
                  <input
                    required
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    data-testid={`signin-${portal.id}-pw`}
                    className="w-full border border-[#0f5c3d]/15 rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                    placeholder="••••••••••"
                  />
                </div>
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid={`signin-${portal.id}-submit`}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0f5c3d] text-white font-medium text-sm hover:bg-[#0b1a2e] transition disabled:opacity-60"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" /> Sign in
                    {portal.id === 'admin'
                      ? (adminTab === 'employee' ? ' as Employee / Staff' : ' as Admin & Management')
                      : ` to ${portal.title.split(' ')[0]}`}
                  </>
                )}
              </button>

              {portal.id === 'books' && (
                <div className="pt-2 text-center">
                  <a href="/books/signup" className="text-xs text-[#0b1a2e]/60 hover:text-[#0f5c3d]">
                    New here? <span className="text-[#0f5c3d] font-medium">Create your Books tenant →</span>
                  </a>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SignInModal;
